import { Card, CardContent, Typography, Box, Tooltip, IconButton, Chip } from '@mui/material';
import { FC } from 'react';
import { TFunction } from 'i18next';
import { AgreementEntity } from 'api/generated';
import { decryptData } from 'utils/encryptionDecryptionAgreements';
import { format } from 'date-fns';
import InfoIcon from '@mui/icons-material/Info';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

interface Props {
  agreements: AgreementEntity[];
  t: TFunction;
  encryptionKey: string;
}

const EmployeeBalance730: FC<Props> = ({ agreements, t, encryptionKey }) => {
  
  // Find the most recent agreement with 730 balance
  const agreementWithBalance = agreements
    .filter(agreement => {
      if (encryptionKey && encryptionKey !== '') {
        return agreement.balance730_encrypted && agreement.balance730_encrypted !== '';
      }
      return agreement.balance730 !== null && agreement.balance730 !== 0;
    })
    .sort((a, b) => {
      const dateA = a.balance730_date ? new Date(a.balance730_date) : new Date(0);
      const dateB = b.balance730_date ? new Date(b.balance730_date) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    })[0];

  if (!agreementWithBalance) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" color="text.secondary">
              {t('employees.report.balance730.title')}
            </Typography>
            <Tooltip title={t('employees.report.balance730.explanation')} arrow>
              <IconButton size="small">
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t('employees.report.balance730.noBalance')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Get the balance value (decrypt if needed)
  const balance = encryptionKey && encryptionKey !== '' && agreementWithBalance.balance730_encrypted
    ? Number(decryptData(agreementWithBalance.balance730_encrypted, encryptionKey))
    : agreementWithBalance.balance730 || 0;

  const isCredit = balance > 0;
  const isDebt = balance < 0;
  const balanceDate = agreementWithBalance.balance730_date 
    ? format(new Date(agreementWithBalance.balance730_date), 'dd/MM/yyyy')
    : null;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" color="text.secondary">
            {t('employees.report.balance730.title')}
          </Typography>
          <Tooltip title={t('employees.report.balance730.explanation')} arrow>
            <IconButton size="small">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          {isCredit && <TrendingUpIcon color="success" />}
          {isDebt && <TrendingDownIcon color="error" />}
          {!isCredit && !isDebt && <AccountBalanceIcon color="action" />}
          
          <Typography variant="h4" fontWeight="bold" color={isCredit ? 'success.main' : isDebt ? 'error.main' : 'text.primary'}>
            {Math.abs(balance).toLocaleString('de-DE', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}€
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={1}>
          <Chip
            label={isCredit ? t('employees.report.balance730.credit') : isDebt ? t('employees.report.balance730.debt') : t('employees.report.balance730.neutral')}
            color={isCredit ? 'success' : isDebt ? 'error' : 'default'}
            size="small"
            variant="outlined"
          />
          
          {balanceDate && (
            <Typography variant="body2" color="text.secondary">
              {t('employees.report.balance730.appliedOn')}: {balanceDate}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmployeeBalance730;
